/* eslint-disable react/no-unused-class-component-methods */
/* eslint-disable react/no-access-state-in-setstate */
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { showToast } from 'stores/ToastStore';
import { Tooltip, OverlayTrigger } from 'react-bootstrap';
import classnames from 'classnames';
import SessionStore from 'stores/SessionStore';
import AccountingWebAPIUtils from 'utils/AccountingWebAPIUtils';
import ShipmentWebAPIUtils from 'utils/ShipmentWebAPIUtils';
import AccountingActionCreators from 'actions/AccountingActionCreators';
import CustomersAPI from 'utils/CustomersAPIUtils';
import PayoutStore from 'stores/accounting/PayoutStore';
import ConfirmModal from 'components/ui/modals/ConfirmModal';
import JobList from 'components/modules/accounting/JobList';
import CargomaticCard from 'components/v2/card';
import CargomaticBadge from 'components/v2/Badge';
import _ from 'underscore';
import CircleCheckIcon from 'components/v2/icons/CircleCheckIcon';
import CircleXIcon from 'components/v2/icons/CircleXIcon';
import CargomaticSelect from 'components/v2/select';
import ShipmentSearch from 'components/site/shipment-search';
import { AuthorizationWrapper } from 'components/wrappers/AuthorizationWrapper';
import { REGIONS } from 'constants/Regions';
import { Translation, withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { unescapeSpecialChars } from 'src/main/js/helpers/helper_functions';
import DownloadIcon from '../../../v2/icons/DownloadIcon';
import EmailIcon from '../../../v2/icons/EmailIcon';
import DollarIcon from '../../../v2/icons/DollarIcon';
import PaymentHistory from '../payment-history';
import PaymentSummary from '../payment-summary';
import LogPaymentModal from '../log-payment-modal';
import SendEmailModal from '../send-email-modal';
import { getPayoutStatusColor } from './colors';

import {
  paymentMethods,
  paymentStatus,
} from '../../../../constants/accounting';
import {
  getSignedUrl,
  getDocuments,
} from '../../../../utils/DocumentsAPIUtils';
import HistoryTable from '../history-table';
import { sluggify } from '../../../../utils/localization/utils';

const setPayoutIdOnPayoutLineItems = (payoutLineItemGroups, payoutId) => {
  payoutLineItemGroups.forEach((payoutLineItemGroup) =>
    payoutLineItemGroup.lineItems?.forEach(function (lineItem) {
      lineItem.payout_id = payoutId;
    }),
  );
};

const getStateFromStores = (id) => {
  const payout = PayoutStore.getPayout(id);
  let balance = 0;
  if (payout) {
    balance = payout.amount_due;
    if (payout.lineItemGroups) {
      setPayoutIdOnPayoutLineItems(payout.lineItemGroups, payout._id);
    }
  }

  return {
    payout: payout,
    balance: balance,
    loading: false,
  };
};

const STRIPE = 'automated';
const ACH = 'manual';

const PAYMENT_METHOD_TOOLTIP_MESSAGE = (
  <Translation>
    {(t) => <>{t('APPROVE_PAYOUTS_MSG_FOR_CARRIERS')}</>}
  </Translation>
);

const INVALID_REPROCESS_STATUS_TOOLTIP = (
  <Tooltip id="tooltip-reprocess-payout">
    <Translation>
      {(t) => <>{t('ONLY_INTERNAL_REVIEW_ELIGIBLE')}</>}
    </Translation>
  </Tooltip>
);

const JOBLIST_ACTIONS = {
  ADD: 'add',
  REMOVE: 'remove',
};

class PayoutDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.getInitialState();
  }

  getInitialState() {
    return {
      loading: true,
      showSendEmailModal: false,
      disableReprocessPayoutButton: false,
      selectedValue: '',
      paymentMethod: null,
      isAdmin: SessionStore.isAdmin(),
      lineItems: [],
      editMode: false,
      updates: [],
      showLineItemConfirmModal: false,
      confirmModal: this.confirmModalDefault(),
      isFinanceAdmin: SessionStore.isFinanceAdmin(),
      hasFinanceAdminRole: SessionStore.hasFinanceAdminRole(),
    };
  }

  componentDidMount() {
    PayoutStore.register(this.onChange);
    AccountingActionCreators.getPayout(this.getPayoutId());
  }

  componentWillUnmount() {
    PayoutStore.unregister(this.onChange);
  }

  UNSAFE_componentWillUpdate(_nextProps, nextState) {
    if (!this.state.payout && nextState.payout) {
      this.populateFundingData(nextState.payout);
    }
  }

  populateFundingData({ carrier: { _id: carrierId } }) {
    CustomersAPI.getBankAccounts(carrierId)
      .then((res) => {
        this.setState((prevState) => {
          const payout = Object.assign({}, prevState.payout);
          const response = res.entity.data;
          payout.carrier.bank_accounts = response.external_accounts;
          return {
            payout,
          };
        });
      })
      .catch((err) => {
        console.log(err.stack || err);
      });
  }

  reprocessCarrierPayout = async () => {
    const { t } = this.props;
    this.setState({ disableReprocessPayoutButton: true }, async () => {
      const {
        payout: { _id },
      } = this.state;

      const response = await AccountingWebAPIUtils.reprocessCarrierPayout(
        _id,
      ).catch((entity) => {
        const err = entity.error;
        if (err) {
          showToast(
            t('REPROCESS_ERROR'),
            t('ERROR_REPROCESSING_WITH_VALUE', { error: err }),
            'error',
          );
        }
      });

      const update = { disableReprocessPayoutButton: false };

      if (response && response.payout) {
        await this.updatePayoutLink();
        update.payout = {
          ...response.payout,
          jobs: response.payout.jobs.map(({ _id }) => _id),
        };
        showToast(
          t('REPROCESS_SUCCESS'),
          t('SUCCESSFULLY_REPROCESSED_PAYOUT'),
          'success',
        );
      }

      this.setState(update);
    });
  };

  waitForDocument = async (foreignKey, attempt = 0) => {
    const response = await getDocuments([foreignKey], null, {
      limit: 1,
      order: {
        field: 'createdAt',
        direction: 'desc',
      },
    });

    if (attempt === 5) {
      return response;
    }

    if (response.data.getDocuments?.documents[0]?.status === 'PENDING') {
      await new Promise((resolve) => {
        setTimeout(() => {
          resolve();
        }, 2000);
      });
      return this.waitForDocument(foreignKey, attempt + 1);
    }

    return response;
  };

  updatePayoutLink = async () => {
    const payoutState = this.state.payout;

    const { data: documentPayout, errors: documentErrors } =
      await this.waitForDocument(payoutState._id);

    if (documentErrors) {
      this.showGetDocumentsErrorToast(documentErrors[0].message);
      return;
    }

    const { data, errors } = await getSignedUrl(
      documentPayout.getDocuments?.documents[0]?.key,
    );

    if (errors) {
      this.showGetSignedUrlErrorToast(errors[0].message);
      return;
    }

    const linkToPayout = data?.getSignedUrl?.signedUrl;

    this.setState({
      linkToPayout,
    });
  };

  getPayoutId() {
    return this.props.payoutId || this.props.match.params.id;
  }

  getLineItems = async (payout) => {
    try {
      const lineItemsResult = await ShipmentWebAPIUtils.getShipmentsLedgers(
        payout.jobs,
        'payout',
      ).catch((e) => console.log('Error: ', e));

      const jobLineItems = payout.lineItemGroups.map((job) => {
        return {
          _id: job.shipment_id,
          payables: job.lineItems.map((lineItem) => ({
            ...lineItem,
            payout_id: payout._id,
          })),
        };
      });

      return payout.status === 'paid'
        ? jobLineItems
        : lineItemsResult.items || [];
    } catch (e) {
      console.log('Get Line Items Error: ', e);
    }
  };
  populateJobPayablesWithLineItemsFromPayout(jobs, lineItemGroups) {
    return jobs.map((job) => {
      const payoutLineItemsForJob = lineItemGroups.find(
        (payoutJob) => payoutJob.shipment_id === job._id,
      );
      job.payables = payoutLineItemsForJob?.lineItems || [];
      return job;
    });
  }

  showGetDocumentsErrorToast = (err) => {
    const { t } = this.props;
    showToast(
      t('GET_DOCUMENT_ERROR'),
      t('GET_DOCUMENT_FAILED_WITH_ERROR', { error: err }),
      'error',
    );
  };

  showGetSignedUrlErrorToast = (err) => {
    const { t } = this.props;
    showToast(
      t('GET_SIGNED_URL_ERROR'),
      t('GET_SIGNED_URL_FAILED_WITH_ERROR', { error: err }),
      'error',
    );
  };

  onChange = async () => {
    const payoutState = getStateFromStores(this.getPayoutId());
    const { data: documentPayout, errors: documentErrors } = await getDocuments(
      [payoutState.payout._id],
      null,
      {
        limit: 1,
        order: {
          field: 'createdAt',
          direction: 'desc',
        },
      },
    );
    if (documentErrors) {
      this.showGetDocumentsErrorToast(documentErrors[0].message);
      return;
    }

    const document = documentPayout.getDocuments?.documents[0];
    if (document) {
      const { data, errors } = await getSignedUrl(document.key);

      if (errors) {
        this.showGetSignedUrlErrorToast(errors[0].message);
        return;
      }
      const linkToPayout = data?.getSignedUrl?.signedUrl;

      payoutState.linkToPayout = linkToPayout;
    }

    payoutState.disableReprocessPayoutButton =
      payoutState.payout.status !== 'internal_review';

    const lineItems = await this.getLineItems(payoutState.payout);

    this.setState({
      ...payoutState,
      lineItems,
    });
  };

  sendEmail() {
    return (to) => {
      AccountingActionCreators.emailPayout(this.state.payout._id, to);
    };
  }

  savePayment() {
    return (payment) => {
      AccountingActionCreators.recordPaymentForPayout(
        this.state.payout._id,
        payment,
      );
    };
  }

  defineDropdownSelectedValue() {
    const {
      paymentMethod,
      payout: { payment_method },
    } = this.state;
    if (paymentMethod) {
      return paymentMethod === 'automated' ? STRIPE : ACH;
    }
    if (payment_method) {
      return payment_method === 'automated' ? STRIPE : ACH;
    }
    return null;
  }

  render() {
    const { t } = this.props;
    if (this.state.loading) {
      return (
        <p data-testid="p-8d6c1304" className="px-4 py-5 sm:p-6">
          {t('LOADING')}
        </p>
      );
    }

    return this.renderPayout();
  }

  approvePayout() {
    return () => {
      this.setState({ showApprovalModal: false }, () => {
        AccountingActionCreators.approvePayout(this.state.payout._id);
      });
    };
  }

  reverseVoid() {
    return () => {
      this.setState({ showVoidModal: false }, () => {
        AccountingActionCreators.reversePayoutVoid(this.state.payout._id);
      });
    };
  }

  voidPayout() {
    return () => {
      this.setState({ showVoidModal: false }, () => {
        AccountingActionCreators.voidPayout(this.state.payout._id);
      });
    };
  }

  reverseApproval() {
    return () => {
      this.setState({ showApprovalModal: false }, () => {
        AccountingActionCreators.reversePayoutApproval(this.state.payout._id);
      });
    };
  }

  handlePaymentMethodSelect = (data) => {
    const { name } = data;
    if (!name) {
      return;
    }
    const method = name.toLowerCase();
    if (method && method !== this.state.paymentMethod) {
      this.setState({ paymentMethod: method });
      const method_value = method === 'manual/ach' ? ACH : STRIPE;
      AccountingWebAPIUtils.setPayoutPaymentMethod(
        method_value,
        this.state.payout._id,
      );
    }
  };

  generateApprovalButton(payout, action, onConfirm) {
    const { t } = this.props;
    return (
      <div data-testid="div-97663b50">
        <button
          data-testid="button-b044db6d"
          className="button button-green-light button-with-icon"
          onClick={() => this.setState({ showApprovalModal: true })}
        >
          <CircleCheckIcon />
          <span data-testid="span-818d03f7">{t(sluggify(action))}</span>
        </button>
        <ConfirmModal
          dataTestId="ConfirmModal-c0408354"
          show={this.state.showApprovalModal}
          title={t('VOID_MODAL_WITH_VALUE', {
            action: t(sluggify(action)),
            payoutID: payout.number,
          })}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showApprovalModal: false })}
          message={t('ARE_YOU_SURE')}
          hideButtonText={t('CANCEL')}
          affirmative={t('OK')}
        />
      </div>
    );
  }

  generateVoidButton(payout, action, onConfirm) {
    const { t } = this.props;
    return (
      <div data-testid="div-9fe1c414">
        <button
          data-testid="button-2f50da3c"
          className="void-payout button button-red-light button-with-icon"
          onClick={() => this.setState({ showVoidModal: true })}
        >
          <CircleXIcon />
          <span data-testid="span-56b91a01">{t(sluggify(action))}</span>
        </button>
        <ConfirmModal
          dataTestId="ConfirmModal-12c14055"
          show={this.state.showVoidModal}
          title={t('VOID_MODAL_WITH_VALUE', {
            action: t(sluggify(action)),
            payoutID: payout.number,
          })}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showVoidModal: false })}
          message={t('ARE_YOU_SURE')}
          hideButtonText={t('CANCEL')}
          affirmative={t('OK')}
        />
      </div>
    );
  }

  renderApprovalButton(payout) {
    const { status } = payout;

    if (
      status === 'payment_initialized' ||
      status === 'paid' ||
      status === 'void'
    ) {
      return '';
    }

    if (status === 'approved_internally') {
      return this.generateApprovalButton(
        payout,
        'Reverse Approval',
        this.reverseApproval(),
      );
    }

    return this.generateApprovalButton(payout, 'Approve', this.approvePayout());
  }

  renderVoidButton(payout) {
    const { status } = payout;

    if (
      status === 'approved_internally' ||
      status === 'payment_initialized' ||
      status === 'paid'
    ) {
      return '';
    }

    if (status === 'void') {
      return this.generateVoidButton(
        payout,
        'Reverse Void',
        this.reverseVoid(),
      );
    }

    return this.generateVoidButton(payout, 'Void', this.voidPayout());
  }

  canMakePayment(payout) {
    return (
      payout.status === 'approved_internally' ||
      payout.status === 'payment_initialized'
    );
  }

  renderPaymentButtons() {
    const { t } = this.props;
    const { payout } = this.state;
    if (!this.canMakePayment(payout)) {
      return null;
    }
    return (
      <div data-testid="div-7c649ea2" className="flex items-center space-x-2">
        <button
          data-testid="button-42a3d745"
          className="button button-green button-with-icon"
          onClick={() => this.setState({ showLogPaymentModal: true })}
          disabled={this.state.balance === 0}
        >
          <DollarIcon />
          <span data-testid="span-380f80dd">{t('RECORD_PAYMENT')}</span>
        </button>
        {this.state.showLogPaymentModal && (
          <LogPaymentModal
            show={this.state.showLogPaymentModal}
            amount={(this.state.balance || 0).toFixed(2)}
            onSave={this.savePayment()}
            onHide={() => this.setState({ showLogPaymentModal: false })}
          />
        )}
      </div>
    );
  }

  setChanges = (updates) => {
    const { editMode } = this.state;
    this.setState({ updates });

    if (!editMode) {
      this.setState({ editMode: true });
    }
  };

  handleEditButton = async () => {
    const payoutState = getStateFromStores(this.getPayoutId());
    const { editMode, payout, updates } = this.state;

    if (!editMode) {
      return this.setState({ editMode: true });
    }
    if (!updates.changes || !updates.changes.length) {
      return this.setState({ editMode: false });
    }
    if (updates.changes && updates.changes.length) {
      try {
        const updatedPayout = await AccountingWebAPIUtils.updatePayoutLineItems(
          payout._id,
          payout.version,
          updates.changes,
        );

        const { data: documentPayout, errors: documentErrors } =
          await getDocuments([payoutState.payout._id], {
            limit: 1,
            order: {
              field: 'createdAt',
              direction: 'desc',
            },
          });

        if (documentErrors) {
          this.showGetDocumentsErrorToast(documentErrors[0].message);
          return;
        }

        const { data, errors } = await getSignedUrl(
          documentPayout.getDocuments?.documents[0]?.key,
        );

        if (errors) {
          this.showGetSignedUrlErrorToast(errors[0].message);
          return;
        }

        const linkToPayout = data?.getSignedUrl?.signedUrl;
        const lineItems = await this.getLineItems(updatedPayout);

        this.setState({
          editMode: false,
          linkToPayout,
          payout: updatedPayout,
          updates: [],
          lineItems,
        });
      } catch (e) {
        console.log(e);
      }
    }
  };

  renderDownloadButton() {
    const { t } = this.props;
    if (!this.state.linkToPayout) {
      return '';
    }
    return (
      <div data-testid="div-cbfb50ae">
        <a
          data-testid="a-6ee980c1"
          href={this.state.linkToPayout}
          target="_blank"
          rel="noopener noreferrer"
          className="button button-gray button-with-icon"
        >
          <DownloadIcon />
          <span data-testid="span-3857bdc8">{t('PAYOUT', { count: 1 })}</span>
        </a>
      </div>
    );
  }

  renderEmailButton() {
    const { t } = this.props;
    return (
      <button
        data-testid="button-a52cb021"
        className="button button-gray button-with-icon"
        onClick={() => {
          this.setState({ showSendEmailModal: true });
        }}
      >
        <EmailIcon />
        <span data-testid="span-6e1e35c5">{titleize(t('EMAIL'))}</span>
      </button>
    );
  }

  handleJobSelect = async (payout, job) => {
    const payoutState = getStateFromStores(this.getPayoutId());
    if (payout.jobs.includes(job._id)) {
      return;
    }

    if (payout.status === 'paid' || payout.status === 'payment_initialized') {
      return;
    }

    this.setState({ showLineItemConfirmModal: false });
    const updatedJobIds = [...payout.jobs, job._id];
    const newPayout = await AccountingWebAPIUtils.updatePayoutJobs(
      payout,
      updatedJobIds,
      JOBLIST_ACTIONS.ADD,
    );

    const { data: documentPayout, errors: documentErrors } = await getDocuments(
      [payoutState.payout._id],
      {
        limit: 1,
        order: {
          field: 'createdAt',
          direction: 'desc',
        },
      },
    );

    if (documentErrors) {
      this.showGetDocumentsErrorToast(documentErrors[0].message);
      return;
    }

    const { data, errors } = await getSignedUrl(
      documentPayout.getDocuments?.documents[0]?.key,
    );

    if (errors) {
      this.showGetSignedUrlErrorToast(errors[0].message);
      return;
    }

    const linkToPayout = data?.getSignedUrl?.signedUrl;

    const lineItems = await this.getLineItems(newPayout);
    this.setState({ payout: newPayout, lineItems, linkToPayout });
  };

  handleJobRemove = async (payout, lineItem) => {
    const payoutState = getStateFromStores(this.getPayoutId());
    const { t } = this.props;
    try {
      const updatedJobIds = payout.jobs.filter(
        (id) => id !== lineItem.shipment_id,
      );
      this.setState({ showLineItemConfirmModal: false });
      const newPayout = await AccountingWebAPIUtils.updatePayoutJobs(
        payout,
        updatedJobIds,
        JOBLIST_ACTIONS.REMOVE,
      );

      showToast(
        t('JOB_REMOVE_SUCCESS'),
        t('SUCCESSFULLY_REMOVED_JOB'),
        'success',
      );

      const { data: documentPayout, errors: documentErrors } =
        await getDocuments([payoutState.payout._id]);

      if (documentErrors) {
        this.showGetDocumentsErrorToast(documentErrors[0].message);
        return;
      }

      const { data, errors } = await getSignedUrl(
        documentPayout.getDocuments?.documents[0]?.key,
      );

      if (errors) {
        this.showGetSignedUrlErrorToast(errors[0].message);
        return;
      }

      const linkToPayout = data?.getSignedUrl?.signedUrl;

      const lineItems = await this.getLineItems(newPayout);
      this.setState({
        payout: newPayout,
        lineItems,
        confirmModal: this.confirmModalDefault(),
        linkToPayout,
      });
    } catch (e) {
      showToast(t('JOB_REMOVE_ERROR'), `${e.entity.error}`, 'error');
    }
  };

  allowPaymentSelection() {
    const {
      hasFinanceAdminRole,
      payout: {
        status,
        carrier: { stripe_account_id },
      },
    } = this.state;
    return (
      hasFinanceAdminRole &&
      stripe_account_id &&
      (status === paymentStatus.APPROVED_INTERNALLY ||
        status === paymentStatus.INTERNAL_REVIEW)
    );
  }

  handleSearch = async (payout, value, job) => {
    const listOfJobIdsAssociated = [...payout.lineItemGroups];
    const searchedShipmentId = job.shipmentId;
    let listOfJobs = [];
    listOfJobs = _.filter(
      listOfJobIdsAssociated,
      (jobsAssociated) => jobsAssociated.shipmentId === searchedShipmentId,
    );
    this.setState({ selectedValue: value });
    if (listOfJobs.length) {
      payout.lineItemGroups = listOfJobs;
      this.setState({ payout: payout });
    }
  };

  onSelectChange = async (value) => {
    const payoutState = await getStateFromStores(this.getPayoutId());
    const { selectedValue } = this.state;
    const lineItemGroups = payoutState.payout.lineItemGroups;
    value = value.trim().toUpperCase();
    if (value.length && value !== selectedValue) {
      let listOfJobs = [];
      listOfJobs = _.filter(lineItemGroups, (lineItemGroup) => {
        if (lineItemGroup.shipmentId.includes(value)) return lineItemGroup;
      });
      if (listOfJobs.length) {
        payoutState.payout.lineItemGroups = listOfJobs;
        this.setState({ payout: payoutState.payout });
      } else {
        payoutState.payout.lineItemGroups = [];
        this.setState({ payout: payoutState.payout });
      }
    } else {
      this.setState(getStateFromStores(this.getPayoutId()));
    }
  };

  confirmModalDefault = () => {
    return {
      title: '',
      message: '',
      onConfirm: null,
    };
  };

  confirmAddShipment = async (job) => {
    try {
      const { t } = this.props;
      const ledgersResult = await ShipmentWebAPIUtils.getShipmentsLedgers(
        [job._id],
        'payout',
      ).catch((e) => {
        throw e;
      });

      const jobLineItems =
        ledgersResult && ledgersResult.items && ledgersResult.items.length
          ? ledgersResult.items[0]
          : {};

      const hasOtherPaidItems = (jobLineItems.payables || []).some(
        (lineItem) => {
          if (!lineItem.payout_id) {
            return false;
          }
          return (
            lineItem.payout &&
            lineItem.payout.status !== 'paid' &&
            lineItem.payout._id.toString() !== this.state.payout._id.toString()
          );
        },
      );

      const hasPaidLineItemsEligibleToMove = jobLineItems.some((lineItem) => {
        if (!lineItem.invoice_id) {
          return true;
        }
        return lineItem.payout && lineItem.payout.status !== 'paid';
      });

      const hasUnpaidLineItems = job.payables.some(
        (payable) => !payable.payout_id,
      );

      if (!hasPaidLineItemsEligibleToMove && !hasUnpaidLineItems) {
        const title = t('UNABLE_TO_ADD_SHIPMENT_TO_PAYOUT_TITLE', {
          shipmentId: job.shipment_id,
        });
        const message = t('UNABLE_TO_ADD_SHIPMENT_TO_PAYOUT_MESSAGE');
        this.setState({
          showLineItemConfirmModal: true,
          confirmModal: {
            title,
            message,
            onConfirm: () =>
              this.setState({
                showLineItemConfirmModal: false,
                confirmModal: this.confirmModalDefault(),
              }),
          },
        });
      } else if (hasOtherPaidItems || hasUnpaidLineItems) {
        const title = t('ADD_SHIPMENT_TO_PAYOUT_TITLE', {
          shipmentId: job.shipment_id,
        });
        const message = t('ADD_SHIPMENT_TO_PAYOUT_MESSAGE');
        this.setState({
          showLineItemConfirmModal: true,
          confirmModal: {
            title,
            message,
            onConfirm: () => this.handleJobSelect(this.state.payout, job),
          },
        });
      } else {
        this.handleJobSelect(this.state.payout, job);
      }
    } catch (error) {
      console.error(error);
    }
  };

  confirmRemoveShipment = (job) => {
    const { t } = this.props;
    const title = t('REMOVE_SHIPMENT_TO_PAYOUT_TITLE', {
      shipmentId: job.shipmentId,
    });
    const message = t('REMOVE_SHIPMENT_TO_PAYOUT_MESSAGE');
    this.setState({
      showLineItemConfirmModal: true,
      confirmModal: {
        title,
        message,
        onConfirm: () => this.handleJobRemove(this.state.payout, job),
      },
    });
  };

  renderPayout() {
    const {
      payout,
      isAdmin,
      confirmModal: { title, message, onConfirm },
    } = this.state;
    const { lineItemGroups } = payout;
    const { t } = this.props;

    const pod_message = payout.missing_pods
      ? t('WITHOUT_PODS_JOBS')
      : t('ALL_PODS_RECEIVED');
    const podBadgeColor = payout.missing_pods ? 'red' : 'green';
    // const display = _.has(payout, 'missing_pods') ? {} : { display: 'none' };
    const reprocessPayoutClass = classnames({
      button: true,
      'button-red': true,
      'button-with-icon': true,
      'pointer-events-none': this.state.disableReprocessPayoutButton,
    });
    return (
      <div
        data-testid="div-2dd250b7"
        className="mx-auto my-8 sm:my-36 xl:my-8 max-w-screen-2xl"
      >
        <div data-testid="div-ebb322ba" className="flex">
          <div data-testid="div-114cd370" className="flex w-2/5">
            <div data-testid="div-bee8b5e6">
              <h2 data-testid="h2-bede8f08" className="my-0 mb-1">
                {payout.number}
              </h2>
              <span
                data-testid="span-08376061"
                className="text-lg text-gray-500"
              >
                {t('DUE_DATE_WITH_VALUE', {
                  dueDate: moment(payout.due_date).format('L'),
                })}
              </span>
            </div>
            <div
              data-testid="div-f7adf3ec"
              className="flex py-2 ml-2 space-x-2"
            >
              <CargomaticBadge
                dataTestId="CargomaticBadge-1a4939eb"
                color={podBadgeColor}
              >
                {pod_message}
              </CargomaticBadge>
              <CargomaticBadge
                dataTestId="CargomaticBadge-4b4cde3d"
                color={getPayoutStatusColor(payout.status)}
              >
                {t(sluggify(payout.status))}
              </CargomaticBadge>
            </div>
          </div>
          <div
            data-testid="div-40433ad6"
            className="flex justify-end w-2/3 items-end mb-9"
          >
            {isAdmin && (
              <div data-testid="div-402caed2" className="px-2">
                <CargomaticSelect
                  dataTestId="CargomaticSelect-383b527d"
                  items={paymentMethods}
                  onSelect={this.handlePaymentMethodSelect}
                  label={t('PAYMENT_METHOD')}
                  disabled={!this.allowPaymentSelection()}
                  caption={t('SELECT_PAYMENT_METHOD')}
                  valueKey="value"
                  textKey="localizationSlug"
                  selected={this.defineDropdownSelectedValue()}
                  info={PAYMENT_METHOD_TOOLTIP_MESSAGE}
                />
              </div>
            )}
          </div>
        </div>
        <div
          data-testid="div-57bd0464"
          className="w-full lg:grid lg:grid-cols-3 lg:gap-4"
        >
          <div
            data-testid="div-8e96762b"
            className="lg:max-h-96 py-4 overflow-y-auto"
          >
            <CargomaticCard
              dataTestId="CargomaticCard-9dc2f9b3"
              header={() => (
                <div data-testid="div-dfadca73">
                  <div data-testid="div-e1f0b576" className="flex w-full">
                    <div data-testid="div-5215d4eb" className="w-1/2">
                      <h3 data-testid="h3-b0498907" className="my-0">
                        {t('PAYOUT_CARRIER_NAME_WITH_VALUE', {
                          carrierName: unescapeSpecialChars(
                            payout.carrier.name,
                          ),
                        })}
                      </h3>
                    </div>
                    <OverlayTrigger
                      placement="top"
                      overlay={INVALID_REPROCESS_STATUS_TOOLTIP}
                    >
                      <div
                        data-testid="div-363ef347"
                        className="flex justify-end w-1/2"
                      >
                        <button
                          data-testid="button-d8ab3b86"
                          className={reprocessPayoutClass}
                          onClick={this.reprocessCarrierPayout}
                          disabled={this.state.disableReprocessPayoutButton}
                        >
                          <CircleCheckIcon />
                          <span data-testid="span-d2144b44">
                            {t('REPROCESS_PAYOUT')}
                          </span>
                        </button>
                      </div>
                    </OverlayTrigger>
                  </div>
                </div>
              )}
            >
              {payout.carrier.phone && (
                <div
                  data-testid="div-7f3a0641"
                  className="mb-6 card--no-x-padding card--no-top-padding"
                >
                  <div
                    data-testid="div-5148e14a"
                    className="px-4 py-4 sm:px-6 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4"
                  >
                    <dt
                      data-testid="dt-e14ad00a"
                      className="text-sm font-medium text-gray-500"
                    >
                      {titleize(t('PHONE'))}
                    </dt>
                    <dd data-testid="dd-0cbb7fb3" className="">
                      {payout.carrier.phone}
                    </dd>
                  </div>
                </div>
              )}
              <div
                data-testid="div-7d5044b2"
                className="mb-4 card--no-x-padding card--no-top-padding"
              >
                <PaymentSummary
                  total={payout.total}
                  paid={payout.total_payments}
                  balance={payout.amount_due}
                  currency={payout.currency}
                />
              </div>
              <div
                data-testid="div-704841dd"
                className="flex flex-wrap items-center mt-4 space-x-2"
              >
                {this.renderApprovalButton(payout)}
                {this.renderVoidButton(payout)}
                <AuthorizationWrapper excludedRegions={[REGIONS.AP]}>
                  {this.renderEmailButton()}
                </AuthorizationWrapper>

                {this.renderDownloadButton()}
                <a
                  data-testid="a-566c3eb2"
                  href={`/payouts/${payout._id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block button button-gray button-with-icon"
                >
                  {t('OPEN_IN_NEW_TAB')}
                </a>
              </div>
            </CargomaticCard>
          </div>
          <div
            data-testid="div-b076ea7a"
            className="lg:max-h-96 py-4 overflow-y-auto"
          >
            <CargomaticCard
              dataTestId="CargomaticCard-a7f622de"
              header={() => (
                <div
                  data-testid="div-0f57e797"
                  className="flex items-center justify-between"
                >
                  <h3 data-testid="h3-96abc8e5" className="my-0">
                    {t('PAYMENTS')}
                  </h3>
                  {this.renderPaymentButtons()}
                </div>
              )}
              className="mb-6"
            >
              <div
                data-testid="div-fde64771"
                className="-mb-4 card--no-x-padding card--no-top-padding"
              >
                <PaymentHistory
                  payments={payout.payments}
                  currency={payout.currency}
                />
              </div>
            </CargomaticCard>
          </div>
          <div
            data-testid="div-eb15461d"
            className="lg:max-h-96 py-4 overflow-y-auto"
          >
            <CargomaticCard
              dataTestId="CargomaticCard-94c2c5ee"
              header={() => (
                <h3 data-testid="h3-245061a4" className="my-0">
                  {t('AUDIT_HISTORY')}
                </h3>
              )}
            >
              <div
                data-testid="div-6d52196e"
                className="-mb-4 card--no-x-padding card--no-top-padding"
              >
                <HistoryTable data={payout.audit || []} type="Payout" />
              </div>
            </CargomaticCard>
          </div>
          <SendEmailModal
            show={this.state.showSendEmailModal}
            onSend={this.sendEmail()}
            onHide={() => {
              this.setState({ showSendEmailModal: false });
            }}
          />
        </div>
        <div data-testid="div-cea48938" className="py-4">
          <CargomaticCard
            dataTestId="CargomaticCard-af31b27e"
            header={() => {
              return (
                <div
                  data-testid="div-9e19c771"
                  className="flex items-center justify-between"
                >
                  <div
                    data-testid="div-f6477618"
                    className="flex items-center space-x-2"
                  >
                    <h3 data-testid="h3-d79fd21e" className="my-0">
                      {t('JOBS')}
                    </h3>
                    <div data-testid="div-16983a25" className="w-60">
                      <ShipmentSearch
                        isFilter
                        lineItemGroups={lineItemGroups}
                        onSelect={(value, job) => {
                          this.handleSearch(payout, value, job);
                        }}
                        onChange={(value) => this.onSelectChange(value)}
                        disabled={!this.state.isFinanceAdmin}
                      />
                    </div>
                  </div>
                </div>
              );
            }}
          >
            <div
              data-testid="div-f6e8f838"
              className="-mb-4 card--no-x-padding card--no-top-padding"
            >
              <JobList
                context="payout"
                ledgerType={payout.type}
                ledgerId={payout._id}
                version={payout.version}
                jobs={payout.lineItemGroups}
                lineItems={this.populateJobPayablesWithLineItemsFromPayout(
                  this.state.lineItems,
                  payout.lineItemGroups,
                )}
                onRemove={this.confirmRemoveShipment}
                editMode={this.state.editMode}
                setChanges={this.setChanges}
                paid={payout.status === 'paid'}
                hideButtons={
                  !this.state.editMode || payout.status !== 'internal_review'
                }
              />
            </div>
          </CargomaticCard>
        </div>
        <ConfirmModal
          dataTestId="ConfirmModal-af080161"
          show={this.state.showLineItemConfirmModal}
          title={title}
          message={message}
          onConfirm={onConfirm}
          onHide={() => this.setState({ showLineItemConfirmModal: false })}
        />
      </div>
    );
  }
}

PayoutDetails.propTypes = {
  match: PropTypes.object,
};

export default withTranslation()(PayoutDetails);
